// Copyright (c) 2026 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DateTime } from 'luxon';

/**
 * Computes a new date when arrow-keying a month or year section, clamping
 * the day to the target month's maximum (e.g., July 31 → June 30).
 */
export function clampedDateForArrowKey(
  currentValue: DateTime,
  key: 'ArrowUp' | 'ArrowDown',
  sectionType: 'M' | 'y'
): DateTime {
  const delta = key === 'ArrowUp' ? 1 : -1;
  let newMonth = currentValue.month;
  let newYear = currentValue.year;

  if (sectionType === 'M') {
    newMonth += delta;
    if (newMonth < 1) {
      newMonth = 12;
      newYear -= 1;
    } else if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    }
  } else {
    newYear += delta;
  }

  const targetDate = DateTime.local(newYear, newMonth);
  const maxDay = targetDate.daysInMonth;
  return currentValue.set({
    year: newYear,
    month: newMonth,
    day: Math.min(currentValue.day, maxDay),
  });
}

/**
 * Determines which date section (M, d, or y) the cursor is in based on
 * separator positions in the displayed text.
 */
export function getSectionType(
  cursorPosition: number,
  displayedText: string,
  format: string
): 'M' | 'd' | 'y' | undefined {
  const seps: number[] = [];
  for (let i = 0; i < displayedText.length; i++) {
    const ch = displayedText[i];
    if (ch === '/' || ch === '-' || ch === '.') {
      seps.push(i);
    }
  }

  let sectionIdx = 0;
  if (seps.length >= 1 && cursorPosition > seps[0]) sectionIdx = 1;
  if (seps.length >= 2 && cursorPosition > seps[1]) sectionIdx = 2;

  const formatParts = format.split(/[/\-. ]/);
  const char = formatParts[sectionIdx]?.[0];
  if (char === 'M' || char === 'd' || char === 'y') {
    return char;
  }
  return undefined;
}
